import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dumbbell, 
  Search, 
  Loader2, 
  Filter,
  ChevronLeft,
  ChevronRight,
  Play,
  Target,
  Lock,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExerciseLibraryPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [totalExercises, setTotalExercises] = useState(0);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [muscles, setMuscles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  
  const ITEMS_PER_PAGE = 20;

  // Check if user has Premium access (admins always have full access)
  useEffect(() => {
    if (user?.plan === 'premium' || user?.role === 'admin') {
      setHasAccess(true);
    } else {
      setHasAccess(false);
      setLoading(false);
    }
  }, [user]);

  // Fetch filter options on mount (only if has access)
  useEffect(() => {
    if (!hasAccess) return;
    
    const fetchFilters = async () => {
      try {
        const token = localStorage.getItem('cardioai_token');
        const headers = { Authorization: `Bearer ${token}` };
        
        const [musclesRes, equipmentRes] = await Promise.all([
          axios.get(`${API}/exercise-library/muscles`, { headers }),
          axios.get(`${API}/exercise-library/equipment`, { headers })
        ]);
        setMuscles(musclesRes.data.muscles || []);
        setEquipment(equipmentRes.data.equipment || []);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, [hasAccess]);

  // Fetch exercises (only if has access)
  useEffect(() => {
    if (!hasAccess) return;
    
    const fetchExercises = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('cardioai_token');
        const params = new URLSearchParams({
          limit: ITEMS_PER_PAGE,
          offset: page * ITEMS_PER_PAGE
        });
        
        if (searchQuery) params.append('search', searchQuery);
        if (selectedMuscle) params.append('muscle', selectedMuscle);
        if (selectedEquipment) params.append('equipment', selectedEquipment);
        
        const response = await axios.get(`${API}/exercise-library?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExercises(response.data.exercises || []);
        setTotalExercises(response.data.total || 0);
      } catch (error) {
        console.error('Error fetching exercises:', error);
        if (error.response?.status === 403) {
          setHasAccess(false);
        } else {
          toast.error('Erro ao carregar exercicios');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchExercises();
  }, [page, selectedMuscle, selectedEquipment, hasAccess]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        setPage(0);
        const fetchSearch = async () => {
          setLoading(true);
          try {
            const params = new URLSearchParams({
              limit: ITEMS_PER_PAGE,
              offset: 0,
              search: searchQuery
            });
            if (selectedMuscle) params.append('muscle', selectedMuscle);
            if (selectedEquipment) params.append('equipment', selectedEquipment);
            
            const token = localStorage.getItem('cardioai_token');
            const response = await axios.get(`${API}/exercise-library?${params}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setExercises(response.data.exercises || []);
            setTotalExercises(response.data.total || 0);
          } catch (error) {
            console.error('Error searching:', error);
          } finally {
            setLoading(false);
          }
        };
        fetchSearch();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleFilterChange = (type, value) => {
    setPage(0);
    if (type === 'muscle') {
      setSelectedMuscle(value === 'all' ? '' : value);
    } else {
      setSelectedEquipment(value === 'all' ? '' : value);
    }
  };

  const clearFilters = () => {
    setSelectedMuscle('');
    setSelectedEquipment('');
    setSearchQuery('');
    setPage(0);
  };

  const totalPages = Math.ceil(totalExercises / ITEMS_PER_PAGE);

  // Muscle name translations
  const muscleLabels = {
    'abductors': 'Abdutores',
    'abs': 'Abdominais',
    'adductors': 'Adutores',
    'biceps': 'Biceps',
    'calves': 'Panturrilhas',
    'cardiovascular system': 'Sistema Cardiovascular',
    'delts': 'Deltoides',
    'forearms': 'Antebracos',
    'glutes': 'Gluteos',
    'hamstrings': 'Isquiotibiais',
    'lats': 'Dorsais',
    'levator scapulae': 'Elevador da Escapula',
    'pectorals': 'Peitorais',
    'quads': 'Quadriceps',
    'serratus anterior': 'Serratil Anterior',
    'spine': 'Coluna',
    'traps': 'Trapezio',
    'triceps': 'Triceps',
    'upper back': 'Costas Superiores'
  };

  // Equipment translations
  const equipmentLabels = {
    'assisted': 'Assistido',
    'band': 'Banda Elastica',
    'barbell': 'Barra',
    'body weight': 'Peso Corporal',
    'bosu ball': 'Bosu Ball',
    'cable': 'Cabo',
    'dumbbell': 'Haltere',
    'elliptical machine': 'Eliptico',
    'ez barbell': 'Barra EZ',
    'hammer': 'Hammer',
    'kettlebell': 'Kettlebell',
    'leverage machine': 'Maquina',
    'medicine ball': 'Bola Medicinal',
    'olympic barbell': 'Barra Olimpica',
    'resistance band': 'Banda de Resistencia',
    'roller': 'Rolo',
    'rope': 'Corda',
    'skierg machine': 'SkiErg',
    'sled machine': 'Sled',
    'smith machine': 'Smith Machine',
    'stability ball': 'Bola de Estabilidade',
    'stationary bike': 'Bicicleta Estacionaria',
    'stepmill machine': 'Stepmill',
    'tire': 'Pneu',
    'trap bar': 'Trap Bar',
    'upper body ergometer': 'Ergometro',
    'weighted': 'Com Peso',
    'wheel roller': 'Roda Abdominal'
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="exercise-library-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Dumbbell className="w-8 h-8 text-[#32CD32]" />
            <h1 
              className="text-3xl md:text-4xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              {t('exerciseLibrary')}
            </h1>
          </div>
          <p className="text-gray-400">
            {t('exerciseLibraryDesc')}
          </p>
        </div>

        {/* Premium Access Check */}
        {!hasAccess && (
          <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30 mb-6">
            <CardContent className="py-12">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6">
                  <Lock className="w-10 h-10 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  Conteúdo Exclusivo Premium
                </h2>
                <p className="text-gray-400 max-w-md mb-6">
                  A Biblioteca de Exercícios com mais de 60 exercícios e vídeos de demonstração é exclusiva para assinantes Premium.
                </p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-8 py-3 rounded-xl hover:opacity-90"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  FAZER UPGRADE PARA PREMIUM
                </Button>
                <p className="text-sm text-gray-500 mt-4">
                  Plano atual: <span className="text-white font-medium capitalize">{user?.plan || 'Grátis'}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {hasAccess && (
          <>
        {/* Filters */}
        <Card className="bg-[#121212] border-white/10 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder={`${t('search')}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 bg-black/50 border-white/20 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              
              {/* Muscle Filter */}
              <Select 
                value={selectedMuscle || 'all'} 
                onValueChange={(value) => handleFilterChange('muscle', value)}
              >
                <SelectTrigger className="w-full md:w-48 h-12 bg-black/50 border-white/20 rounded-xl" data-testid="muscle-filter">
                  <Filter className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder={t('filterByMuscle')} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/20 max-h-80">
                  <SelectItem value="all">{t('allMuscles')}</SelectItem>
                  {muscles.map(muscle => (
                    <SelectItem key={muscle} value={muscle}>
                      {muscleLabels[muscle] || muscle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Equipment Filter */}
              <Select 
                value={selectedEquipment || 'all'} 
                onValueChange={(value) => handleFilterChange('equipment', value)}
              >
                <SelectTrigger className="w-full md:w-48 h-12 bg-black/50 border-white/20 rounded-xl" data-testid="equipment-filter">
                  <Dumbbell className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder={t('filterByEquipment')} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/20 max-h-80">
                  <SelectItem value="all">{t('allEquipment')}</SelectItem>
                  {equipment.map(eq => (
                    <SelectItem key={eq} value={eq}>
                      {equipmentLabels[eq] || eq}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Clear Filters */}
              {(selectedMuscle || selectedEquipment || searchQuery) && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                >
                  Limpar
                </Button>
              )}
            </div>
            
            {/* Results count */}
            <div className="mt-4 text-sm text-gray-500">
              {totalExercises} {t('exercisesFound')}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
          </div>
        ) : exercises.length === 0 ? (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="py-20 text-center">
              <Dumbbell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">{t('noExercisesFound')}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {exercises.map(exercise => {
                // Check if it's a YouTube URL
                const isYouTube = exercise.gifUrl && exercise.gifUrl.includes('youtube.com');
                const youtubeId = isYouTube ? exercise.gifUrl.split('/embed/')[1]?.split('?')[0] : null;
                const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
                
                return (
                <Card 
                  key={exercise.id} 
                  className="bg-[#121212] border-white/10 hover:border-[#32CD32]/50 transition-all cursor-pointer group overflow-hidden"
                  onClick={() => setSelectedExercise(exercise)}
                >
                  {/* Preview */}
                  <div className="relative aspect-square bg-black/50 overflow-hidden">
                    {thumbnailUrl ? (
                      // YouTube thumbnail
                      <div className="relative w-full h-full">
                        <img 
                          src={thumbnailUrl} 
                          alt={exercise.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                            <Play className="w-6 h-6 text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                    ) : exercise.gifUrl && !isYouTube ? (
                      // Regular GIF/image from API
                      <img 
                        src={exercise.gifUrl} 
                        alt={exercise.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      // No media - show icon
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#32CD32]/10 to-black/50">
                        <Dumbbell className="w-16 h-16 text-[#32CD32]/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <Button size="sm" className="bg-[#32CD32] text-black hover:bg-[#2ab32a]">
                        <Play className="w-4 h-4 mr-1" />
                        {t('viewExercise')}
                      </Button>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="font-medium text-sm line-clamp-2 mb-2 capitalize">
                      {exercise.name}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      <span className="px-2 py-0.5 bg-[#32CD32]/20 text-[#32CD32] rounded-full text-xs">
                        {muscleLabels[exercise.target] || exercise.target}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                        {equipmentLabels[exercise.equipment] || exercise.equipment}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/10">
              <Button
                variant="ghost"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-gray-400"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              <span className="text-sm text-gray-500">
                Pagina {page + 1} de {totalPages || 1}
              </span>
              <Button
                variant="ghost"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="text-gray-400"
              >
                Proxima
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        )}

        {/* Exercise Detail Modal */}
        {selectedExercise && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedExercise(null)}>
            <Card className="w-full max-w-2xl bg-[#121212] border-white/10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="relative aspect-video bg-black">
                {(() => {
                  const isYouTube = selectedExercise.gifUrl && selectedExercise.gifUrl.includes('youtube.com');
                  
                  if (isYouTube) {
                    // Embedded YouTube video
                    return (
                      <iframe
                        src={selectedExercise.gifUrl}
                        title={selectedExercise.name}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  } else if (selectedExercise.gifUrl) {
                    // Regular GIF/image
                    return (
                      <img 
                        src={selectedExercise.gifUrl} 
                        alt={selectedExercise.name}
                        className="w-full h-full object-contain"
                      />
                    );
                  } else {
                    // No media
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#32CD32]/10 to-black/50">
                        <Dumbbell className="w-24 h-24 text-[#32CD32]/30" />
                      </div>
                    );
                  }
                })()}
              </div>
              
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold capitalize mb-4">{selectedExercise.name}</h2>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-black/30 rounded-lg p-4">
                    <div className="text-xs text-gray-500 mb-1">Musculo Alvo</div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#32CD32]" />
                      <span className="capitalize">{muscleLabels[selectedExercise.target] || selectedExercise.target}</span>
                    </div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-4">
                    <div className="text-xs text-gray-500 mb-1">Equipamento</div>
                    <div className="flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-blue-400" />
                      <span className="capitalize">{equipmentLabels[selectedExercise.equipment] || selectedExercise.equipment}</span>
                    </div>
                  </div>
                </div>
                
                {selectedExercise.bodyPart && (
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 mb-2">Parte do Corpo</div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm capitalize">
                      {selectedExercise.bodyPart}
                    </span>
                  </div>
                )}
                
                {selectedExercise.secondaryMuscles && selectedExercise.secondaryMuscles.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 mb-2">Musculos Secundarios</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedExercise.secondaryMuscles.map(muscle => (
                        <span key={muscle} className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs capitalize">
                          {muscleLabels[muscle] || muscle}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedExercise.instructions && selectedExercise.instructions.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Instrucoes</div>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
                      {selectedExercise.instructions.map((instruction, idx) => (
                        <li key={idx}>{instruction}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                <Button 
                  className="w-full mt-6" 
                  variant="ghost"
                  onClick={() => setSelectedExercise(null)}
                >
                  {t('close')}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExerciseLibraryPage;
